// @flow

import * as React from "react";
import { useDispatch } from "react-redux";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import Config from "../../constants/Config";
import LoadEwaySDK from "../../utils/LoadEwaySDK";
import useAsyncState from "../../hooks/useAsyncState";
import formatCurrency from "../../utils/formatCurrency";
import { payOrder } from "../../api/cart";
import { checkoutOrder } from "../../actions/cart";
import ServerErrorMessage from "./ServerErrorMessage";
import Button from "./Button";
import Loader from "./Loader";

type PropsType = { payAmount: number, onSuccess: Function };

var fieldStyles = `
    height: 1.5em;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
    box-sizing: border-box;
    color: black;
    font-size: 16px;
    line-height: 24px;
`;

const EwayFieldTemplate = (props: { label?: string, fieldDivId?: string }) => {
    return (
        <div
            className="react-form__field 
                react-form__field--text
                is-focused
            "
        >
            <label htmlFor={props.fieldDivId}>
                <div className="react-form__field__label">
                    <div className="react-form__field__label__inner">
                        {props.label}
                    </div>
                </div>
                <div id={props.fieldDivId}></div>
            </label>
        </div>
    );
};

const EwaySecureFields = (props: PropsType): React.Node => {
    const dispatch = useDispatch();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [loaded, setLoaded] = React.useState(false);
    const [state, localActions] = useAsyncState();
    const [secureFieldCode, setSecureFieldCode] = React.useState(null);
    const [fields] = React.useState([
        {
            publicApiKey: Config.ewayApiKey,
            fieldDivId: "eway-secure-field-name",
            fieldType: "name",
            styles: fieldStyles,
            label: "Card Name",
        },
        {
            publicApiKey: Config.ewayApiKey,
            fieldDivId: "eway-secure-field-card",
            fieldType: "card",
            styles: fieldStyles,
            label: "Card Number",
        },
        {
            publicApiKey: Config.ewayApiKey,
            fieldDivId: "eway-secure-field-expirytext",
            fieldType: "expirytext",
            styles: fieldStyles,
            label: "Card Expiry",
        },
        {
            publicApiKey: Config.ewayApiKey,
            fieldDivId: "eway-secure-field-cvn",
            fieldType: "cvn",
            styles: fieldStyles,
            label: "CVC/CCV",
        },
    ]);

    React.useEffect(() => {
        LoadEwaySDK(true).then(() => {
            initEway();
        });
    }, []); // eslint-disable-line

    const initEway = () => {
        if (window.eWAY) {
            setLoaded(false);
            setSecureFieldCode(null);

            fields.forEach(fieldConfig => {
                // clear container field config div
                const existingField = document.getElementById(
                    fieldConfig.fieldDivId
                );
                if (existingField) {
                    existingField.innerHTML = "";
                }

                // init way into container
                window.eWAY.setupSecureField(fieldConfig, secureFieldCallback);
            });

            setTimeout(() => {
                setLoaded(true);
            }, 1000);
        }
    };

    const secureFieldCallback = event => {
        if (!event.fieldValid) {
            console.error(event.errors);

            // localActions.fail(event.errors);
        } else {
            // set secure field code in state ready for submission
            setSecureFieldCode(event.secureFieldCode);
        }
    };

    const handleEwaySubmit = async () => {
        localActions.request();

        if (loaded && secureFieldCode) {
            if (!executeRecaptcha) {
                localActions.fail(
                    "reCAPTCHA is still loading. Please try again."
                );
                return;
            }

            try {
                const recaptchaToken = await executeRecaptcha("checkout");

                const checkoutSuccessResponse = await dispatch(checkoutOrder())
                    .then(() => payOrder(secureFieldCode, recaptchaToken));

                if (props.onSuccess) {
                    localActions.success();
                    props.onSuccess(checkoutSuccessResponse);
                }
            } catch (error) {
                console.error(error);

                if (error?.httpStatus === 429) {
                    localActions.fail(
                        "Too many failed payment attempts. Please wait 10 minutes before trying again."
                    );
                    return;
                }

                setLoaded(false);

                // re-init eway secure fields
                LoadEwaySDK(true).then(() => {
                    initEway();
                });

                localActions.fail(
                    error.message ||
                        "There was an issue processing your transaction."
                );
            }
        } else {
            localActions.fail(
                "Please complete filling out your credit card details."
            );
        }
    };

    const getFieldConfigProps = (id: string) => {
        return fields.find(field => field.fieldDivId === id);
    };

    return (
        <div className="eway-secure-fields">
            <div className="eway-secure-fields__inputs">
                <Loader active={!loaded}>
                    <div className="row row--padding-small">
                        <div className="col-xs-12">
                            <EwayFieldTemplate
                                {...getFieldConfigProps(
                                    "eway-secure-field-name"
                                )}
                            />
                        </div>

                        <div className="col-xs-12">
                            <EwayFieldTemplate
                                {...getFieldConfigProps(
                                    "eway-secure-field-card"
                                )}
                            />
                        </div>

                        <div className="col-xs-7">
                            <EwayFieldTemplate
                                {...getFieldConfigProps(
                                    "eway-secure-field-expirytext"
                                )}
                            />
                        </div>
                        <div className="col-xs-5">
                            <EwayFieldTemplate
                                {...getFieldConfigProps(
                                    "eway-secure-field-cvn"
                                )}
                            />
                        </div>
                    </div>
                </Loader>
            </div>

            <ServerErrorMessage>{state.error}</ServerErrorMessage>

            <Button
                onClick={handleEwaySubmit}
                type="button"
                styles={["green"]}
                iconBefore="lock"
                loading={state.loading}
                success={!!state.success}
            >
                Pay {formatCurrency(props.payAmount)}
            </Button>
        </div>
    );
};

export default EwaySecureFields;
